import React, { useState } from 'react';
import { Linkedin, MapPin, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import TiccAIlogo from '../assets/TiccAIlogo.png';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSubscriptionStatus('idle');
    setErrorMessage('');

    try {
      const { error } = await supabase
        .from('newsletter_subscriptions')
        .insert([{ email }]);

      if (error) {
        if (error.code === '23505') { // Unique violation
          setErrorMessage('You are already subscribed to our newsletter!');
        } else {
          setErrorMessage('Something went wrong. Please try again.');
        }
        setSubscriptionStatus('error');
      } else {
        setSubscriptionStatus('success');
        setEmail('');
      }
    } catch (err) {
      setErrorMessage('Something went wrong. Please try again.');
      setSubscriptionStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-[#060a13] py-12 mt-auto relative z-10 font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Logo and Links Section */}
          <div className="lg:col-span-8 grid grid-cols-4 gap-8">
            {/* Logo and Location */}
            <div className="col-span-4 sm:col-span-1">
              <div className="flex flex-col gap-4">
                <img src={TiccAIlogo} alt="TiccAI" className="h-8 w-8" />
                <div className="flex items-center gap-2 text-gray-400 font-sans">
                  <MapPin className="h-4 w-4" />
                  <span className="text-sm">Ann Arbor, MI</span>
                </div>
              </div>
            </div>

            {/* Product Links */}
            <div className="col-span-2 sm:col-span-1">
              <h3 className="text-white font-medium mb-3 text-sm font-sans">Product</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors text-sm font-sans">
                    Home
                  </a>
                </li>
                <li>
                  <a href="#features" className="text-gray-400 hover:text-white transition-colors text-sm font-sans">
                    Features
                  </a>
                </li>
                <li>
                  <a href="/pricing" className="text-gray-400 hover:text-white transition-colors text-sm font-sans">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="/contact" className="text-gray-400 hover:text-white transition-colors text-sm font-sans">
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Links */}
            <div className="col-span-2 sm:col-span-1">
              <h3 className="text-white font-medium mb-3 text-sm font-sans">Company</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors text-sm font-sans">
                    About Us
                  </a>
                </li>
                <li className="relative group">
                  <span className="text-gray-400 cursor-default text-sm font-sans">
                    Careers
                  </span>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-[#0A1220] border border-white/[0.05] rounded-lg text-sm text-gray-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap font-sans">
                    We are not currently hiring
                  </div>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div className="col-span-2 sm:col-span-1">
              <h3 className="text-white font-medium mb-3 text-sm font-sans">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/privacy" className="text-gray-400 hover:text-white transition-colors text-sm font-sans">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/security" className="text-gray-400 hover:text-white transition-colors text-sm font-sans">
                    Data Security
                  </a>
                </li>
                <li>
                  <a href="/terms" className="text-gray-400 hover:text-white transition-colors text-sm font-sans">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Subscribe and Social Section */}
          <div className="lg:col-span-4">
            <div className="bg-white/[0.02] rounded-lg p-6 border border-white/[0.05]">
              <h2 className="text-white font-medium mb-2 font-sans">Stay Updated</h2>
              <p className="text-gray-400 text-sm mb-4 font-sans">
                Subscribe to our newsletter for the latest updates and features.
              </p>
              {subscriptionStatus === 'success' ? (
                <div className="flex items-center gap-2 text-green-500 mb-6 font-sans">
                  <Check className="h-5 w-5" />
                  <span className="text-sm">Thanks for subscribing!</span>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="mb-6">
                  <div className="flex gap-2 mb-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="flex-1 px-3 py-2 bg-white/[0.03] border border-white/[0.1] rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm font-sans"
                      required
                      disabled={isLoading}
                    />
                    <button 
                      type="submit"
                      className="bg-gradient-to-r from-pink-500 to-pink-600 text-white px-4 py-2 rounded-md hover:from-pink-600 hover:to-pink-700 transition-all font-medium text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed font-sans"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Subscribing...
                        </span>
                      ) : (
                        'Subscribe'
                      )}
                    </button>
                  </div>
                  {subscriptionStatus === 'error' && (
                    <p className="text-red-500 text-sm font-sans">{errorMessage}</p>
                  )}
                </form>
              )}
              <div className="flex items-center gap-4">
                <a 
                  href="https://www.linkedin.com/company/ticcai/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-gray-400 hover:text-white transition-colors font-sans"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Border Line */}
        <div className="mt-12 border-t border-white/[0.05]" />
        
        {/* Copyright Notice */}
        <div className="mt-8 text-center">
          <p className="text-gray-400 text-sm font-sans">
            © {new Date().getFullYear()} TiccAI. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;